import Api from 'entities/helpers/api';
import PropTypes from 'prop-types';
import Decorator from 'clientDecorator';
import InteractionFormSolicitors from 'apps/interactions/components/create/interaction-form-solicitors';
import { useEffect } from 'react';
import { useIdentity } from 'base/identity-resolver';
import { getContactIsSolicitor } from 'apps/portfolio-performance/portfolio-queries';

import { useInteractionFormContext } from 'apps/interactions/form/context/interaction-form-context';

// Helper function to see if the user is a solicitor for the org
const contactIsSolicitor = async (contactId) => {
  try {
    return await Api.CONTACTS.SEARCH.post({
      params: { limit: 1 },
      data: JSON.stringify(getContactIsSolicitor(contactId)),
      success: (results) => {
        if (results.items.length) {
          return true;
        }
        return false;
      },
      error: () => {
        return false;
      },
    });
  } catch (e) {}
};

const SolicitorField = ({
  onChange,
  onBlur,
  name,
  value,
  enableInteractionSolicitorType,
  required = false,
  field,
  meta,
  helpers,
}) => {
  const { user, org } = useIdentity();
  const { hasRmAssignments, editing } = useInteractionFormContext();

  // When a user opens a form for a new interaction, attempt to set them as the Primary Solicitor
  useEffect(() => {
    const setUserAsPrimarySolicitor = async () => {
      // Check if user is affiliated with the org
      const affiliation = Decorator.User.getAffiliationFor(user, org.id);
      if (!affiliation) return;

      const userData = {
        remote_user_id: affiliation.remote_user_id,
        name: user.name,
        user_id: user.id,
        type: 'PRIMARY',
      };

      if (!hasRmAssignments) {
        // This means the org is on the "old" Assignments Framework (should be rare)
        onChange({ target: { name, value: [userData] } });
      } else if (affiliation?.contact_id) {
        try {
          // If the user is a solicitor at the organization, set them as the Primary Solicitor for the interaction
          const isSolicitor = await contactIsSolicitor(affiliation.contact_id);
          if (isSolicitor) {
            onChange({ target: { name, value: [userData] } });
          }
        } catch (e) {
          console.error({ e });
        }
      }
    };

    if (!editing) {
      setUserAsPrimarySolicitor();
    }
  }, [editing, user, org, hasRmAssignments, name, onChange]);

  return (
    <InteractionFormSolicitors
      interaction_solicitor={value}
      handleFormChange={(name, newVal) => onChange({ target: { name, value: newVal } })}
      enableInteractionSolicitorType={enableInteractionSolicitorType}
      onClose={() => {
        onBlur({ target: { name } });
      }}
    />
  );
};

SolicitorField.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.array,
  enableInteractionSolicitorType: PropTypes.bool,
  required: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.any,
    checked: PropTypes.bool,
    multiple: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    initialError: PropTypes.string,
    initialTouched: PropTypes.bool,
    initialValue: PropTypes.any,
    touched: PropTypes.bool,
    value: PropTypes.any,
  }),
  helpers: PropTypes.shape({
    setValue: PropTypes.func,
    setTouched: PropTypes.func,
    setError: PropTypes.func,
  }),
};

export default SolicitorField;
